import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { upload } from 'src/helpers/upLoadHelper';
import { FaqDocument } from 'src/components/Faqs/types';
import {
  addFaqsNotesDocuments,
  deleteFaqsNotesDocuments,
  fetchFaqsNotesDocuments,
  fetchSuiteFaqs,
  updateFaqsNotesDocuments,
} from 'src/services/client';
import { FaqFormData } from 'src/components/Faqs/CustomHooks/useFaqForm';
import { parseFAQData } from 'src/utils/CommonHelpers/commonhelpers';

type ValidationStatus = 'idle' | 'success' | 'error';
type SaveAction = 'SaveAndClose' | 'SaveAndAdd';
type EntityType = 'Suite' | 'Item' | 'Asset';

interface UseFaqOperationsConfig {
  itemId: string | number;
  userId: string;
  userRole?: string;
  entityType: EntityType;
  assetId: any;
}

export interface UseFaqOperationsReturn {
  faqs: FaqDocument[];
  isLoadingFaqs: boolean;

  saveFaq: (faq: FaqFormData, action: SaveAction) => Promise<void>;
  deleteFaq: (faq: any) => Promise<void>;
  refetchFaqs: () => Promise<void>;

  isSaving: boolean;
  validationStatus: ValidationStatus;
  lastSaveAction: SaveAction | null;
  resetValidation: () => void;
  assetFaqDocuments: any;
}

/**
 * TECH DEBT HELPER:
 * The backend endpoints return a "mixed bucket" of Manuals (PDFs), FAQs, and Notes.
 * We must client-side filter this to only return actual FAQs to avoid rendering issues.
 *
 * Criteria for Valid FAQ:
 * - name === 'Frequently Asked Questions'
 * - OR Type === 'FAQS'
 * - OR documentType === 'Frequently Asked Questions'
 */
const filterMixedFaqResponse = (rawDocs: any[]): any[] => {
  if (!Array.isArray(rawDocs)) return [];

  return rawDocs.filter(
    (doc: any) =>
      doc.name === 'Frequently Asked Questions' ||
      doc.Type === 'FAQS' ||
      doc.documentType === 'Frequently Asked Questions'
  );
};

/**
 * TECH DEBT WARNING:
 * The backend for FAQs is highly inconsistent.
 * - WRITE PATH: Unified. saveFaq and deleteFaq use the same endpoints/actions.
 * - READ PATH: Fragmented. Each entity type requires a unique API endpoint.
 * - TODO: Standardize the Read API to use a unified GET endpoint by EntityType/Id.
 */
const useFaqOperations = ({
  itemId,
  userId,
  userRole,
  entityType,
  assetId,
}: UseFaqOperationsConfig): UseFaqOperationsReturn => {
  const [suiteFaqs, setSuiteFaqs] = useState<FaqDocument[]>([]);
  const [itemFaqs, setItemFaqs] = useState<FaqDocument[]>([]);
  const [assetFaqDocuments, setAssetFaqDocuments] = useState<any[]>([]);

  const [isLoadingFaqs, setIsLoadingFaqs] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationStatus, setValidationStatus] =
    useState<ValidationStatus>('idle');
  const [lastSaveAction, setLastSaveAction] = useState<SaveAction | null>(null);

  const resetValidation = useCallback(() => {
    setValidationStatus('idle');
    setLastSaveAction(null);
  }, []);

  const prepareFaqAttachmentData = useCallback(
    async (faq: FaqFormData): Promise<FaqFormData> => {
      let preparedFaq = { ...faq };

      // Only upload if `image` is a freshly-staged File[] from the form.
      // When editing an existing FAQ, `image` is a persisted S3 URL string
      // and must be left untouched, otherwise `upload(url[0])` would destroy it.
      const [stagedFile] = Array.isArray(faq.image) ? faq.image : [];
      const hasStagedFile: boolean = stagedFile instanceof File;

      if (hasStagedFile) {
        try {
          const uploadedUrl: any = await upload(stagedFile);
          preparedFaq = { ...preparedFaq, image: uploadedUrl };
        } catch (error) {
          console.error('File upload failed:', error);
          throw new Error(
            'File upload failed. Please check your connection and try again.'
          );
        }
      }

      const fieldsToTrim: (keyof FaqFormData)[] = [
        'question',
        'issue',
        'linkTitle',
        'caption',
      ];

      fieldsToTrim.forEach((field: keyof FaqFormData) => {
        const value = preparedFaq[field];

        if (value && typeof value === 'string') {
          (preparedFaq[field] as string) = value.trim();
        }
      });

      return preparedFaq;
    },
    []
  );

  /**
   * SHARED FETCH LOGIC:
   * Abstracts the common pattern of:
   * 1. Checking itemId
   * 2. Setting loading state (optional)
   * 3. Calling API
   * 4. Normalizing array response
   * 5. Filtering out non-FAQs
   * 6. Parsing properties
   * 7. Setting state
   */
  const handleFetch = useCallback(
    async (
      apiCall: (id: string | number) => Promise<any>,
      setState: React.Dispatch<React.SetStateAction<any[]>>,
      withLoading: boolean
    ) => {
      if (!itemId) return;

      if (withLoading) setIsLoadingFaqs(true);

      try {
        const response: any = await apiCall(itemId);

        if (response.status === 200) {
          const data: any = await response.json();
          // Normalize: Response might be an array OR an object with a 'documents' array
          const rawDocs: any = Array.isArray(data) ? data : data.documents || [];

          const filteredDocs: any[] = filterMixedFaqResponse(rawDocs);
          const parsedAssets: any = filteredDocs.map((doc: any) =>
            parseFAQData(doc)
          );

          setState(parsedAssets);
        }
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      } finally {
        if (withLoading) setIsLoadingFaqs(false);
      }
    },
    [itemId]
  );

  // Note: Asset fetch didn't originally use global loading state, passing false to preserve behavior
  const fetchAssetFAQs = useCallback(async () => {
    await handleFetch(fetchFaqsNotesDocuments, setAssetFaqDocuments, false);
  }, [handleFetch]);

  const fetchSuiteFAQs = useCallback(async () => {
    await handleFetch(fetchSuiteFaqs, setSuiteFaqs, true);
  }, [handleFetch]);

  const fetchItemFAQs = useCallback(async () => {
    await handleFetch(fetchFaqsNotesDocuments, setItemFaqs, true);
  }, [handleFetch]);

  const refreshData = useCallback(async () => {
    switch (entityType) {
      case 'Asset':
        return fetchAssetFAQs();
      case 'Suite':
        return fetchSuiteFAQs();
      case 'Item':
        return fetchItemFAQs();
      default:
        console.warn(`No fetcher defined for ${entityType}`);
    }
  }, [entityType, fetchAssetFAQs, fetchSuiteFAQs, fetchItemFAQs]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const saveFaq = useCallback(
    async (faq: FaqFormData, action: SaveAction) => {
      setIsSaving(true);
      setLastSaveAction(action);

      try {
        const preparedFaq: FaqFormData = await prepareFaqAttachmentData(faq);
        const jsonData = JSON.stringify(preparedFaq).toString();
        let response: any;

        /* TODO: TECH DEBT - BRITTLE CONTRACT / SHARED ID
         * Legacy Behavior: Assets do not have their own FAQs; they display their parent Item's FAQs.
         * The legacy code forced `ItemId: asset.item.id`.
         * * If we use `assetId` here, we create a "private" FAQ bucket for this specific unit that
         * the Master Catalog (Item Page) cannot see
         * * FIX: Always prioritize `itemId` (Master ID) if available, even on Asset screens,
         * to maintain bidirectional sync between Asset and Item views.
         * https://app.asana.com/1/41213529744312/project/1208853227838817/task/1213046580243262?focus=true */
        const targetId = itemId || assetId;

        if (preparedFaq.id) {
          // UPDATE PATH.
          const updateParams = {
            id: preparedFaq.id,
            ItemId: targetId,
            Name: 'Frequently Asked Questions',
            Type: 'FAQS',
            data: jsonData,
            UserId: userId,
            Role: userRole,
          };
          response = await updateFaqsNotesDocuments(updateParams);
        } else {
          // CREATE PATH.
          const createParams = {
            ItemId: targetId,
            Name: 'Frequently Asked Questions',
            Type: 'FAQS',
            Data: jsonData,
            UserId: userId,
            Role: userRole,
          };
          response = await addFaqsNotesDocuments(createParams);
        }

        if (response.ok || response.status === 200) {
          toast.success(`ⓘ FAQ ${preparedFaq.id ? 'updated' : 'added'}.`);
          setValidationStatus('success');
          await refreshData();
        }
      } catch (e) {
        setValidationStatus('error');
        console.error('Save FAQ Error:', e);
      } finally {
        setIsSaving(false);
      }
    },
    [itemId, assetId, entityType, userId, userRole, refreshData, prepareFaqAttachmentData]
  );

  const deleteFaq = useCallback(
    async (faq: any) => {
      const faqId = faq.id || faq.Id;

      if (!faqId) {
        toast.error('Could not find ID for deletion.');
        return;
      }

      setIsSaving(true);

      try {
        const response: any = await deleteFaqsNotesDocuments(faqId);

        if (response.ok || response.status === 200) {
          toast.success('ⓘ FAQ deleted.');
          await refreshData();
        }
      } catch (error) {
        console.error('Delete FAQ Error:', error);
        toast.error('Failed to delete FAQ.');
      } finally {
        setIsSaving(false);
      }
    },
    [refreshData]
  );

  const getActiveFaqs = () => {
    if (entityType === 'Asset') {
      return assetFaqDocuments;
    }

    if (entityType === 'Suite') {
      return suiteFaqs;
    }

    return itemFaqs;
  };

  return {
    faqs: getActiveFaqs(),
    isLoadingFaqs,
    saveFaq,
    deleteFaq,
    refetchFaqs: refreshData,
    isSaving,
    validationStatus,
    lastSaveAction,
    resetValidation,
    assetFaqDocuments,
  };
};

export default useFaqOperations;